import { inject } from '@angular/core';
import { selectSelectedAccountId } from '@mkp/account/state';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { SentryService } from '@mkp/core/util-sentry';
import { creditApiActions, creditManagementPageActions } from '@mkp/credit/actions';
import { CreditOverviewActions } from '@mkp/credit/feature-credit-overview/actions';
import { productSummaryPageActions } from '@mkp/product-summary/actions';
import { ProductOfferingActions } from '@mkp/product/feature-product-offering/actions';
import { getCurrentAvailableCreditsQuery } from '@mkp/shared/util-product';
import { CreditResource } from '@mkp/shared/data-access';
import { CreditCreateInput, HIGH_LIMIT, toUUID } from '@mkp/shared/models';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { AppConfig } from '@mkp/shared/util-app-config';
import { WorkspaceRouteService } from '@mkp/shared/util-router';
import { ErrorState } from '@mkp/shared/util-state';
import { FeatureDashboardActions } from '@mkp/user/feature-dashboard/actions';
import { createVacancyWrapperActions, listVacancyActions } from '@mkp/vacancy/state/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, defer, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { getPublicationDaysViolation } from './publication-days-violation';
import { filterAvailableCreditsForNonSlots } from './slot-helper';

const SENTRY_MESSAGES = {
  corrected: 'Credit publication days rejected by core-api and corrected by retry',
  retryRejected: 'Credit publication days retry rejected by core-api',
  severalAllowed: 'Credit publication days violation names several allowed values',
} as const;

const retryWithAllowedPublicationDays = (
  credit: CreditCreateInput,
  error: unknown,
  creditResource: CreditResource,
  sentryService: SentryService
): Observable<Action> => {
  const violation = getPublicationDaysViolation(error);
  const productType = credit.product.type;
  const sentDays = credit.product.publicationDays;

  if (violation.kind === 'absent') {
    return of(creditApiActions.addCreditFailed({ error: error as ErrorState }));
  }

  if (violation.kind === 'unusable') {
    sentryService.captureException(SENTRY_MESSAGES.severalAllowed, {
      productType,
      sentDays,
      allowedValues: violation.allowedValues,
    });
    return of(creditApiActions.addCreditFailed({ error: error as ErrorState }));
  }

  const { allowedDays } = violation;

  return creditResource
    .add({ ...credit, product: { ...credit.product, publicationDays: allowedDays } })
    .pipe(
      tap(() =>
        sentryService.captureWarning(SENTRY_MESSAGES.corrected, {
          productType,
          sentDays,
          allowedDays,
        })
      ),
      map(() => creditApiActions.addCreditSucceed()),
      catchError((retryError: unknown) => {
        sentryService.captureException(SENTRY_MESSAGES.retryRejected, {
          productType,
          sentDays,
          allowedDays,
        });
        return of(creditApiActions.addCreditFailed({ error: retryError as ErrorState }));
      })
    );
};

export const addCredit = createEffect(
  (
    actions$ = inject(Actions),
    creditResource = inject(CreditResource),
    sentryService = inject(SentryService)
  ) =>
    actions$.pipe(
      ofType(creditApiActions.addCredit),
      switchMap(({ credit }) =>
        creditResource.add(credit).pipe(
          map(() => creditApiActions.addCreditSucceed()),
          catchError((error: unknown) =>
            defer(() =>
              retryWithAllowedPublicationDays(credit, error, creditResource, sentryService)
            ).pipe(
              catchError(() => of(creditApiActions.addCreditFailed({ error: error as ErrorState })))
            )
          )
        )
      )
    ),
  { functional: true }
);

export const addCreditSuccess = createEffect(
  (actions$ = inject(Actions), snackbarService = inject(SnackbarService)) =>
    actions$.pipe(
      ofType(creditApiActions.addCreditSucceed),
      tap(() => snackbarService.show('ADD_CREDIT.SNACKBAR.SUCCESS'))
    ),
  { functional: true, dispatch: false }
);

export const addCreditFailure = createEffect(
  (actions$ = inject(Actions), snackbarService = inject(SnackbarService)) =>
    actions$.pipe(
      ofType(creditApiActions.addCreditFailed),
      tap(() => snackbarService.showError('ADD_CREDIT.SNACKBAR.ERROR'))
    ),
  { functional: true, dispatch: false }
);

export const loadAvailableCreditsWithWallets = createEffect(
  (actions$ = inject(Actions), store = inject(Store), creditResource = inject(CreditResource)) =>
    actions$.pipe(
      ofType(
        CreditOverviewActions.enter,
        FeatureDashboardActions.enter,
        ProductOfferingActions.enter,
        createVacancyWrapperActions.enter,
        creditManagementPageActions.enter,
        creditManagementPageActions.loadCredits,
        listVacancyActions.load,
        listVacancyActions.loadCredits,
        productSummaryPageActions.enter
      ),
      switchMap(() => store.select(selectSelectedAccountId).pipe(filter(Boolean))),
      switchMap((selectedAccountId) => {
        return creditResource
          .getWithQuery({
            params: getCurrentAvailableCreditsQuery(HIGH_LIMIT),
            context: getOptionalContext(),
            headers: { maxRetry: 1 },
            withAccountId: selectedAccountId,
          })
          .pipe(
            map((response) => response._embedded.results),
            map(filterAvailableCreditsForNonSlots),
            map((credits) => creditApiActions.loadCreditsSucceed({ credits })),
            catchError((error) => of(creditApiActions.loadCreditsFailed({ error })))
          );
      })
    ),
  { functional: true }
);

export const loadCreditTotalCount = createEffect(
  (actions$ = inject(Actions), creditResource = inject(CreditResource), store = inject(Store)) =>
    actions$.pipe(
      ofType(creditApiActions.loadCreditTotalCount),
      concatLatestFrom(() => store.select(selectSelectedAccountId).pipe(filter(Boolean), take(1))),
      switchMap(([_, accountId]) =>
        creditResource
          .getWithQuery({
            params: {
              limit: 0,
            },
            withAccountId: accountId,
          })
          .pipe(
            map(({ totalCount }) => creditApiActions.loadCreditTotalCountSucceed({ totalCount })),
            catchError((error) => of(creditApiActions.loadCreditTotalCountFailed({ error })))
          )
      )
    ),
  { functional: true }
);

export const loadCreditCount = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(creditManagementPageActions.enter),
      map(() => creditApiActions.loadCreditTotalCount())
    ),
  { functional: true }
);

export const loadFreeCreditUsageThisYear = createEffect(
  (actions$ = inject(Actions), creditResource = inject(CreditResource), store = inject(Store)) =>
    actions$.pipe(
      ofType(ProductOfferingActions.enter),
      concatLatestFrom(() => store.select(selectSelectedAccountId).pipe(filter(Boolean), take(1))),
      switchMap(([, accountId]) =>
        creditResource.hasUsedFreeCreditThisYear(accountId).pipe(
          map((used) => creditApiActions.loadFreeCreditUsageThisYearSucceed({ used })),
          catchError((error) => of(creditApiActions.loadFreeCreditUsageThisYearFailed({ error })))
        )
      )
    ),
  { functional: true }
);

export const extendCredit = createEffect(
  (actions$ = inject(Actions), creditResource = inject(CreditResource)) =>
    actions$.pipe(
      ofType(creditApiActions.extendCredit),
      switchMap(({ creditId, payload }) =>
        creditResource.extendCredit(toUUID(creditId), payload).pipe(
          map(() => creditApiActions.extendCreditSucceed()),
          catchError((error) => of(creditApiActions.extendCreditFailed({ error })))
        )
      )
    ),
  { functional: true }
);

export const extendCreditSuccess = createEffect(
  (
    actions$ = inject(Actions),
    snackbarService = inject(SnackbarService),
    workspaceRoute = inject(WorkspaceRouteService)
  ) =>
    actions$.pipe(
      ofType(creditApiActions.extendCreditSucceed),
      tap(() => snackbarService.show('EXTEND_CREDIT.SNACKBAR.SUCCESS')),
      tap(() =>
        workspaceRoute.navigate([
          AppConfig.routes.creditManagement,
          AppConfig.routes.purchaseHistory,
        ])
      )
    ),
  { functional: true, dispatch: false }
);

export const extendCreditFailure = createEffect(
  (actions$ = inject(Actions), snackbarService = inject(SnackbarService)) =>
    actions$.pipe(
      ofType(creditApiActions.extendCreditFailed),
      tap(() => snackbarService.showError('EXTEND_CREDIT.SNACKBAR.ERROR'))
    ),
  { functional: true, dispatch: false }
);
